/**
 * Availability Requests Panel
 *
 * Single collapsible panel that lives below the All/Received/Sent outer
 * tabs on the Referrals page. Replaces the legacy
 * `AvailabilityRequestResponsesCard` and `ReceivedAvailabilityRequestsCard`
 * cards.
 *
 * Behavior:
 * - Header: "Availability Requests" with subtitle "X For You · Y Sent"
 * - Body (when expanded): inner For You / Sent tabs
 *   - For You: pending + accepted sections, decline (with confirmation)
 *   - Sent: clickable cards open recipient modal with selectable accepted
 *     responders. Timed-out / no-responders state surfaces "Send To More
 *     Users" + "Cancel Request" CTAs (cancel requires confirmation).
 *   - "Send To More Partners" opens a connections picker modal with name
 *     search, alphabetical sort, and trade-priority grouping (matching
 *     trade first, then "All Partners").
 */

import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { featureFlags } from '@root-config/features/feature-flags';
import {
  Briefcase,
  Calendar,
  CheckCircle,
  ChevronDown,
  Clock,
  EyeOff,
  MapPin,
  RefreshCw,
  Search,
  Send,
  Users,
  XCircle,
} from 'lucide-react';
import { api } from '../lib/api';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Skeleton } from './ui/skeleton';
import { useToast } from '../contexts/ToastContext';
import { useTradeCategories } from '../hooks/useTradeCategories';
import { ConfirmModal } from './ConfirmModal';
import { Modal } from './Modal';
import { AvailabilityRequestResponseModal } from './AvailabilityRequestResponseModal';
import { AvailabilityRequestDetailsModal } from './AvailabilityRequestDetailsModal';

// ---------- Types ----------

type ResponseStatus = 'pending' | 'available' | 'unavailable';

interface ReceivedRequest {
  id: string;
  senderId: string;
  senderName?: string | null;
  senderCompany?: string | null;
  jobDescription?: string | null;
  jobLocation?: string | null;
  expiresAt: string;
  status: string;
  isClosed?: boolean;
  createdAt?: string | null;
  responseStatus: ResponseStatus;
  responseMessage?: string | null;
  respondedAt?: string | null;
  recipientRecordId?: string;
}

interface SentRequestRecipient {
  id: string;
  recipientId: string;
  recipientName?: string | null;
  recipientTrade?: string | null;
  recipientProfileImage?: string | null;
  responseStatus: ResponseStatus;
  responseMessage?: string | null;
  respondedAt?: string | null;
}

interface SentRequest {
  id: string;
  senderId: string;
  jobDescription: string;
  jobLocation: string;
  tradeIds?: number[] | null;
  requestedAt: string;
  expiresAt: string;
  status: string;
  createdAt: string;
  recipients: SentRequestRecipient[];
}

interface Connection {
  id: string;
  fullName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  company?: string | null;
  primaryTrade?: string | null;
  trades?: string[] | null;
  profileImageUrl?: string | null;
}

interface AvailabilityRequestsPanelProps {
  /** Outer tab from ReferralsPage: 'all' | 'received' | 'sent' */
  outerTab: 'all' | 'received' | 'sent';
}

// ---------- Helpers ----------

const formatTimeRemaining = (expiresAt: string | undefined | null): string => {
  if (!expiresAt) return 'No expiration';
  const now = new Date();
  const expiry = new Date(expiresAt);
  const diff = expiry.getTime() - now.getTime();

  if (diff < 0) return 'Expired';

  const hours = Math.floor(diff / (1000 * 60 * 60));
  if (hours < 1) return 'Less than 1 hour';
  if (hours === 1) return '1 hour remaining';
  if (hours < 24) return `${hours} hours remaining`;

  const days = Math.floor(hours / 24);
  return days === 1 ? '1 day remaining' : `${days} days remaining`;
};

const isExpired = (expiresAt: string | undefined | null): boolean => {
  if (!expiresAt) return false;
  return new Date(expiresAt).getTime() < Date.now();
};

const compareNamesAsc = (a?: string | null, b?: string | null) =>
  (a || '').localeCompare(b || '', undefined, { sensitivity: 'base' });

const getConnectionDisplayName = (connection: Connection): string => {
  if (connection.fullName?.trim()) return connection.fullName.trim();
  const first = connection.firstName?.trim() || '';
  const last = connection.lastName?.trim() || '';
  const combined = `${first} ${last}`.trim();
  return combined || connection.company || 'Unknown Partner';
};

const connectionMatchesTrade = (connection: Connection, tradeName: string): boolean => {
  const target = tradeName.toLowerCase();
  if (connection.primaryTrade && connection.primaryTrade.toLowerCase() === target) return true;
  return (connection.trades || []).some((t) => (t || '').toLowerCase() === target);
};

// ---------- Component ----------

export const AvailabilityRequestsPanel = ({ outerTab }: AvailabilityRequestsPanelProps) => {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  // Default panel open state mirrors which inner tab is meaningful for the outer tab.
  const defaultInnerTab: 'forYou' | 'sent' = outerTab === 'sent' ? 'sent' : 'forYou';
  // Auto-expand the panel when deep-linked with ?section=availability-requests
  // (e.g. from a reopened-availability-request notification click).
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinked = searchParams.get('section') === 'availability-requests';
  const [isOpen, setIsOpen] = useState<boolean>(deepLinked);
  const [innerTab, setInnerTab] = useState<'forYou' | 'sent'>(defaultInnerTab);

  // Sync inner tab when outer tab changes (only when panel makes one tab irrelevant)
  useEffect(() => {
    if (outerTab === 'received') setInnerTab('forYou');
    if (outerTab === 'sent') setInnerTab('sent');
  }, [outerTab]);

  // When the deep-link param appears (or changes), open the panel and then
  // clear the param so a subsequent collapse isn't immediately re-opened by
  // the stale URL state.
  useEffect(() => {
    if (!deepLinked) return;
    setIsOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete('section');
    setSearchParams(next, { replace: true });
  }, [deepLinked, searchParams, setSearchParams]);

  // ---------- Data fetching ----------

  const receivedQuery = useQuery({
    queryKey: ['availability-requests', 'received'],
    queryFn: async () => {
      const response = await api.get('/api/workflows/availability-request/received');
      if (response?.success && response.data) return response.data as ReceivedRequest[];
      return (Array.isArray(response) ? response : []) as ReceivedRequest[];
    },
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  });

  const sentQuery = useQuery({
    queryKey: ['availability-requests', 'sent', 'list'],
    queryFn: async () => {
      const response = await api.get('/api/workflows/availability-request/list/sent');
      if (response?.success && response.data) return response.data as SentRequest[];
      return [] as SentRequest[];
    },
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  });

  const { data: tradeCategories } = useTradeCategories();

  // ---------- Decline received request flow ----------

  const [pendingDecline, setPendingDecline] = useState<ReceivedRequest | null>(null);

  const declineMutation = useMutation({
    mutationFn: async (request: ReceivedRequest) => {
      // Record the decline and stop there. This used to also call /hide, which
      // erased the decline from the recipient's own history — declining is a
      // decision, not a dismissal. It now moves to the Declined filter instead.
      // (/hide remains available as an explicit, user-initiated "hide this".)
      await api.put(`/api/workflows/availability-request/${request.id}/respond`, {
        responseStatus: 'unavailable',
        responseMessage: '',
      });
    },
    onSuccess: () => {
      showSuccess('Request Declined', 'Moved to your Declined list');
      queryClient.invalidateQueries({ queryKey: ['availability-requests'] });
      queryClient.invalidateQueries({ queryKey: ['/api/workflows/needs-attention'] });
      setPendingDecline(null);
    },
    onError: (error: any) => {
      showError('Failed to Decline', error?.message || 'Please try again');
      setPendingDecline(null);
    },
  });

  // ---------- Dismiss received request flow ----------
  // Hides the request from the recipient's view (via hidden_by_recipient) without
  // changing their response. Silent — the sender is not notified.

  const [pendingDismiss, setPendingDismiss] = useState<ReceivedRequest | null>(null);

  const dismissMutation = useMutation({
    mutationFn: async (request: ReceivedRequest) => {
      await api.put(`/api/workflows/availability-request/${request.id}/hide`, { hidden: true });
    },
    onSuccess: () => {
      showSuccess('Request Dismissed', 'Hidden from your list');
      queryClient.invalidateQueries({ queryKey: ['availability-requests'] });
      queryClient.invalidateQueries({ queryKey: ['/api/workflows/needs-attention'] });
      setPendingDismiss(null);
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : 'Please try again';
      showError('Failed to Dismiss', message);
      setPendingDismiss(null);
    },
  });

  // ---------- Respond modal (existing modal) ----------

  const [respondModal, setRespondModal] = useState<{
    isOpen: boolean;
    data: any | null;
  }>({ isOpen: false, data: null });

  // ---------- Accepted-request details modal ----------

  const [detailsRequest, setDetailsRequest] = useState<ReceivedRequest | null>(null);

  const openRespondModal = (request: ReceivedRequest) => {
    setRespondModal({
      isOpen: true,
      data: {
        availabilityRequestId: request.id,
        jobDescription: request.jobDescription || '',
        cityState: request.jobLocation || '',
        availabilityWindow: formatTimeRemaining(request.expiresAt),
        jobLocation: request.jobLocation || '',
        expiresAt: request.expiresAt,
        responseStatus: request.responseStatus,
        responseMessage: request.responseMessage || undefined,
      },
    });
  };

  // ---------- Reopen expired request flow (sender-only) ----------

  const reopenMutation = useMutation({
    mutationFn: async ({ requestId, hoursToExtend }: { requestId: string; hoursToExtend: number }) => {
      return api.post(`/api/workflows/availability-request/${requestId}/reopen`, {
        hoursToExtend,
      });
    },
    onSuccess: (_data, variables) => {
      const { hoursToExtend } = variables;
      const label =
        hoursToExtend === 1
          ? '1 hour'
          : hoursToExtend < 24
            ? `${hoursToExtend} hours`
            : hoursToExtend === 24
              ? '1 day'
              : `${Math.round(hoursToExtend / 24)} days`;
      showSuccess('Request Reopened', `Deadline extended by ${label}.`);
      queryClient.invalidateQueries({ queryKey: ['availability-requests'] });
      setOpenSentRequestId(null);
    },
    onError: (error: any) => {
      showError('Failed to Reopen Request', error?.message || 'Please try again');
    },
  });

  // ---------- Sent: recipients modal ----------

  const [openSentRequestId, setOpenSentRequestId] = useState<string | null>(null);
  const [selectedRecipientId, setSelectedRecipientId] = useState<string | null>(null);
  const [pendingCancelId, setPendingCancelId] = useState<string | null>(null);
  const [partnerPickerOpenForId, setPartnerPickerOpenForId] = useState<string | null>(null);

  const openSentRequest = sentQuery.data?.find((r) => r.id === openSentRequestId) || null;

  // Reset selection whenever the modal opens for a new request
  useEffect(() => {
    setSelectedRecipientId(null);
  }, [openSentRequestId]);

  const sendReferralMutation = useMutation({
    mutationFn: async ({ requestId, recipientId }: { requestId: string; recipientId: string }) => {
      return api.post(`/api/workflows/availability-request/${requestId}/convert-to-referral`, {
        recipientId,
      });
    },
    onSuccess: () => {
      showSuccess('Referral Sent!', 'The availability request has been converted to a referral');
      queryClient.invalidateQueries({ queryKey: ['availability-requests'] });
      queryClient.invalidateQueries({ queryKey: ['/api/workflows/referral/list'] });
      setOpenSentRequestId(null);
    },
    onError: (error: any) => {
      showError('Failed to Send Referral', error?.message || 'Please try again');
    },
  });

  const cancelMutation = useMutation({
    mutationFn: async (requestId: string) => {
      return api.delete(`/api/workflows/availability-request/${requestId}`);
    },
    onSuccess: () => {
      showSuccess('Request Cancelled', 'The availability request has been cancelled');
      queryClient.invalidateQueries({ queryKey: ['availability-requests'] });
      setPendingCancelId(null);
      setOpenSentRequestId(null);
    },
    onError: (error: any) => {
      showError('Failed to Cancel Request', error?.message || 'Please try again');
      setPendingCancelId(null);
    },
  });

  // ---------- Dismiss sent request flow ----------
  // Hides the request from the sender's Sent list (via hidden_by_sender) without
  // notifying recipients or affecting their view of the request.

  const [pendingSentDismiss, setPendingSentDismiss] = useState<SentRequest | null>(null);

  const dismissSentMutation = useMutation({
    mutationFn: async (request: SentRequest) => {
      await api.put(`/api/workflows/availability-request/${request.id}/hide-sender`, {
        hidden: true,
      });
    },
    onSuccess: (_data, request) => {
      showSuccess('Request Dismissed', 'Hidden from your Sent list');
      queryClient.invalidateQueries({ queryKey: ['availability-requests'] });
      setPendingSentDismiss(null);
      if (openSentRequestId === request.id) {
        setOpenSentRequestId(null);
      }
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : 'Please try again';
      showError('Failed to Dismiss', message);
      setPendingSentDismiss(null);
    },
  });

  const addRecipientsMutation = useMutation({
    mutationFn: async ({ requestId, recipientIds }: { requestId: string; recipientIds: string[] }) => {
      return api.post(`/api/workflows/availability-request/${requestId}/add-recipients`, {
        recipientIds,
      });
    },
    onSuccess: (data: any) => {
      const added = data?.data?.addedCount ?? 0;
      const renotified = data?.data?.renotifiedCount ?? 0;
      const parts: string[] = [];
      if (added > 0) parts.push(`Sent to ${added} new partner${added === 1 ? '' : 's'}`);
      if (renotified > 0) parts.push(`re-pinged ${renotified} pending partner${renotified === 1 ? '' : 's'}`);
      showSuccess(
        'Partners Added',
        parts.length ? parts.join(' · ') : 'No new partners to send to'
      );
      queryClient.invalidateQueries({ queryKey: ['availability-requests'] });
      setPartnerPickerOpenForId(null);
    },
    onError: (error: any) => {
      showError('Failed to Add Partners', error?.message || 'Please try again');
    },
  });

  // ---------- Derived: For You buckets ----------

  const forYouRequests = (receivedQuery.data || []).filter((r) => !r.isClosed);

  // Pending only counts requests that are still answerable — an expired one is not
  // actionable (the server now rejects a response to it), so it belongs under Expired.
  const pendingRequests = forYouRequests.filter(
    (r) => r.responseStatus === 'pending' && !isExpired(r.expiresAt)
  );
  const acceptedRequests = forYouRequests.filter((r) => r.responseStatus === 'available');

  // History buckets. Both are derived, not stored: a decline is response_status
  // 'unavailable', and expiry is computed from expires_at — there is no persisted
  // 'expired' status anywhere, so nothing needed migrating to support this.
  const declinedRequests = forYouRequests.filter((r) => r.responseStatus === 'unavailable');
  const expiredRequests = forYouRequests.filter(
    (r) => r.responseStatus === 'pending' && isExpired(r.expiresAt)
  );

  // ---------- Derived: Sent counts ----------

  const sentRequests = sentQuery.data || [];
  const visibleSentRequests = sentRequests.filter(
    (r) => r.status === 'active' || r.status === 'pending'
  );

  const forYouCount = pendingRequests.length + acceptedRequests.length;
  const sentCount = visibleSentRequests.length;

  const isLoading = receivedQuery.isPending || sentQuery.isPending;

  // ---------- Render ----------

  return (
    <Card className="border border-gray-200 bg-white/80 backdrop-blur-md shadow-md mb-6">
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-gray-50/60 rounded-2xl transition-colors"
        aria-expanded={isOpen}
      >
        <div className="flex items-center gap-3">
          <Users className="w-5 h-5 text-amber-500" />
          <div>
            <h3 className="text-lg font-semibold text-gray-900 leading-tight">
              Availability Requests
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              <span className="font-medium text-gray-700">{forYouCount}</span> For You
              <span className="mx-2 text-gray-300">·</span>
              <span className="font-medium text-gray-700">{sentCount}</span> Sent
            </p>
          </div>
        </div>
        <ChevronDown
          className={`w-5 h-5 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <CardContent className="pt-0 pb-5 px-5">
          {/* Inner tabs */}
          <div className="flex gap-2 mb-4">
            <button
              type="button"
              onClick={() => setInnerTab('forYou')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                innerTab === 'forYou'
                  ? 'bg-blue-500 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              For You ({forYouCount})
            </button>
            <button
              type="button"
              onClick={() => setInnerTab('sent')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                innerTab === 'sent'
                  ? 'bg-blue-500 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Sent ({sentCount})
            </button>
          </div>

          {isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          ) : innerTab === 'forYou' ? (
            <ForYouView
              pending={pendingRequests}
              accepted={acceptedRequests}
              declined={declinedRequests}
              expired={expiredRequests}
              onRespond={openRespondModal}
              onDecline={(req) => setPendingDecline(req)}
              onDismiss={(req, needsConfirmation) => {
                if (needsConfirmation) setPendingDismiss(req);
                else dismissMutation.mutate(req);
              }}
              onOpenDetails={(req) => setDetailsRequest(req)}
            />
          ) : (
            <SentView
              requests={visibleSentRequests}
              onOpenRequest={(id) => setOpenSentRequestId(id)}
              onDismiss={(req) => setPendingSentDismiss(req)}
            />
          )}
        </CardContent>
      )}

      {/* Decline confirmation */}
      <ConfirmModal
        isOpen={!!pendingDecline}
        onClose={() => setPendingDecline(null)}
        onConfirm={() => pendingDecline && declineMutation.mutate(pendingDecline)}
        title="Decline Availability Request?"
        message="Declining will remove this request from your list. The sender will be notified that you are unavailable."
        confirmText="Decline Request"
        cancelText="Keep"
        isDangerous
        confirmLoading={declineMutation.isPending}
      />

      {/* Dismiss confirmation (pending/accepted — declined/expired dismiss without confirm) */}
      <ConfirmModal
        isOpen={!!pendingDismiss}
        onClose={() => setPendingDismiss(null)}
        onConfirm={() => pendingDismiss && dismissMutation.mutate(pendingDismiss)}
        title="Dismiss Availability Request?"
        message={
          pendingDismiss?.responseStatus === 'available'
            ? 'You accepted this request. Dismissing hides it from your list — the sender will not be notified and can still send you a referral for it.'
            : 'This hides the request from your list. The sender will not be notified. You can still respond later if it shows up again.'
        }
        confirmText="Dismiss"
        cancelText="Keep"
        confirmLoading={dismissMutation.isPending}
      />

      {/* Respond modal (reuses existing flow) */}
      <AvailabilityRequestResponseModal
        isOpen={respondModal.isOpen}
        onClose={() => setRespondModal({ isOpen: false, data: null })}
        requestData={respondModal.data}
      />

      {/* Accepted-request details modal */}
      <AvailabilityRequestDetailsModal
        isOpen={!!detailsRequest}
        onClose={() => setDetailsRequest(null)}
        request={detailsRequest}
        onChangeResponse={(req) => {
          setDetailsRequest(null);
          openRespondModal(req as ReceivedRequest);
        }}
      />

      {/* Sent recipient detail modal */}
      {openSentRequest && (
        <SentRequestDetailModal
          request={openSentRequest}
          isOpen={!!openSentRequest}
          onClose={() => setOpenSentRequestId(null)}
          selectedRecipientId={selectedRecipientId}
          onSelectRecipient={setSelectedRecipientId}
          onSendReferral={() => {
            if (!selectedRecipientId) return;
            sendReferralMutation.mutate({
              requestId: openSentRequest.id,
              recipientId: selectedRecipientId,
            });
          }}
          onSendToMore={() => setPartnerPickerOpenForId(openSentRequest.id)}
          onCancelRequest={() => setPendingCancelId(openSentRequest.id)}
          onDismissRequest={() => setPendingSentDismiss(openSentRequest)}
          onReopenRequest={(hoursToExtend) =>
            reopenMutation.mutate({ requestId: openSentRequest.id, hoursToExtend })
          }
          isSendingReferral={sendReferralMutation.isPending}
          isReopening={reopenMutation.isPending}
        />
      )}

      {/* Cancel sent confirmation */}
      <ConfirmModal
        isOpen={!!pendingCancelId}
        onClose={() => setPendingCancelId(null)}
        onConfirm={() => pendingCancelId && cancelMutation.mutate(pendingCancelId)}
        title="Cancel Availability Request?"
        message="This will close the request completely. Recipients will be notified, and the request will no longer appear in your list."
        confirmText="Cancel Request"
        cancelText="Keep Request"
        isDangerous
        confirmLoading={cancelMutation.isPending}
      />

      {/* Dismiss sent confirmation — silent to recipients, reversible in DB */}
      <ConfirmModal
        isOpen={!!pendingSentDismiss}
        onClose={() => setPendingSentDismiss(null)}
        onConfirm={() =>
          pendingSentDismiss && dismissSentMutation.mutate(pendingSentDismiss)
        }
        title="Dismiss Sent Availability Request?"
        message={(() => {
          if (!pendingSentDismiss) return '';
          const expired = isExpired(pendingSentDismiss.expiresAt);
          const pendingRecipientCount = pendingSentDismiss.recipients.filter(
            (r) => r.responseStatus === 'pending'
          ).length;
          if (expired || pendingRecipientCount === 0) {
            return 'This hides the request from your Sent list. It will not notify recipients.';
          }
          return 'This hides the request from your Sent list. Any responses that come in later will not appear here — cancel the request instead if you don\'t want to hear back.';
        })()}
        confirmText="Dismiss"
        cancelText="Keep"
        confirmLoading={dismissSentMutation.isPending}
      />

      {/* Send-to-more partner picker */}
      {partnerPickerOpenForId && (
        <PartnerPickerModal
          isOpen={!!partnerPickerOpenForId}
          onClose={() => setPartnerPickerOpenForId(null)}
          existingRecipientIds={
            sentRequests.find((r) => r.id === partnerPickerOpenForId)?.recipients.map((rec) => rec.recipientId) || []
          }
          jobTradeName={(() => {
            const req = sentRequests.find((r) => r.id === partnerPickerOpenForId);
            if (!req) return null;
            // Preferred: explicit trade IDs on the request. Compare as
            // strings since trade_categories.id is a UUID (Number() yields NaN).
            const ids = (req.tradeIds || []).map((v: unknown) => String(v));
            if (ids.length && tradeCategories) {
              const match = tradeCategories.find((c) => ids.includes(String(c.id)));
              if (match?.name) return match.name;
            }
            // Fallback: infer the requested trade from the existing recipients'
            // primary trades. The most common trade among recipients reflects
            // the trade the sender curated for this request. (Seed data does
            // not yet populate trade_ids — this is the reliable signal.)
            const counts = new Map<string, number>();
            for (const rec of req.recipients || []) {
              const t = rec.recipientTrade?.trim();
              if (t) counts.set(t, (counts.get(t) || 0) + 1);
            }
            if (!counts.size) return null;
            return [...counts.entries()].sort((a, b) => b[1] - a[1])[0][0];
          })()}
          onConfirm={(recipientIds) =>
            partnerPickerOpenForId &&
            addRecipientsMutation.mutate({ requestId: partnerPickerOpenForId, recipientIds })
          }
          isSubmitting={addRecipientsMutation.isPending}
        />
      )}
    </Card>
  );
};

// ---------- For You sub-view ----------

interface ForYouViewProps {
  pending: ReceivedRequest[];
  accepted: ReceivedRequest[];
  declined: ReceivedRequest[];
  expired: ReceivedRequest[];
  onRespond: (req: ReceivedRequest) => void;
  onDecline: (req: ReceivedRequest) => void;
  onDismiss: (req: ReceivedRequest, needsConfirmation: boolean) => void;
  onOpenDetails: (req: ReceivedRequest) => void;
}

const ForYouView = ({
  pending,
  accepted,
  declined,
  expired,
  onRespond,
  onDecline,
  onDismiss,
  onOpenDetails,
}: ForYouViewProps) => {
  // History (declined / expired) is collapsed by default so the working view stays
  // clean, but it is no longer thrown away — it is one click behind this toggle.
  const [showHistory, setShowHistory] = useState(false);

  // Gated off for now. The data is still preserved either way — this only controls
  // whether the recipient sees the two history sections.
  const historyEnabled = featureFlags.showAvailabilityRequestHistory;
  const historyCount = historyEnabled ? declined.length + expired.length : 0;

  if (pending.length === 0 && accepted.length === 0 && historyCount === 0) {
    return (
      <div className="bg-gray-50 rounded-lg p-6 text-center">
        <p className="text-gray-600 text-sm">
          No requests for you. Partners checking your availability will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {pending.length > 0 && (
        <div>
          <SectionHeader label={`Pending (${pending.length})`} accent="amber" />
          <div className="space-y-3">
            {pending.map((req) => (
              <ReceivedRequestCard
                key={req.id}
                request={req}
                onRespond={() => onRespond(req)}
                onDecline={() => onDecline(req)}
                onDismiss={(needsConfirmation) => onDismiss(req, needsConfirmation)}
                onOpenDetails={() => onOpenDetails(req)}
              />
            ))}
          </div>
        </div>
      )}

      {accepted.length > 0 && (
        <div>
          <SectionHeader label={`Accepted (${accepted.length})`} accent="green" />
          <div className="space-y-3">
            {accepted.map((req) => (
              <ReceivedRequestCard
                key={req.id}
                request={req}
                onRespond={() => onRespond(req)}
                onDecline={() => onDecline(req)}
                onDismiss={(needsConfirmation) => onDismiss(req, needsConfirmation)}
                onOpenDetails={() => onOpenDetails(req)}
              />
            ))}
          </div>
        </div>
      )}

      {historyCount > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setShowHistory((v) => !v)}
            className="text-sm font-medium text-gray-600 hover:text-gray-900"
          >
            {showHistory ? 'Hide' : 'Show'} history ({historyCount})
          </button>

          {showHistory && (
            <div className="space-y-5 mt-3">
              {declined.length > 0 && (
                <div>
                  <SectionHeader label={`Declined (${declined.length})`} accent="gray" />
                  <div className="space-y-3">
                    {declined.map((req) => (
                      <ReceivedRequestCard
                        key={req.id}
                        request={req}
                        onRespond={() => onRespond(req)}
                        onDecline={() => onDecline(req)}
                        onDismiss={(needsConfirmation) => onDismiss(req, needsConfirmation)}
                        onOpenDetails={() => onOpenDetails(req)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {expired.length > 0 && (
                <div>
                  <SectionHeader label={`Expired (${expired.length})`} accent="gray" />
                  <div className="space-y-3">
                    {expired.map((req) => (
                      <ReceivedRequestCard
                        key={req.id}
                        request={req}
                        onRespond={() => onRespond(req)}
                        onDecline={() => onDecline(req)}
                        onDismiss={(needsConfirmation) => onDismiss(req, needsConfirmation)}
                        onOpenDetails={() => onOpenDetails(req)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const SectionHeader = ({ label, accent }: { label: string; accent: 'amber' | 'green' | 'gray' }) => (
  <div className="flex items-center gap-2 mb-2">
    <span
      className={`w-2 h-2 rounded-full ${
        accent === 'amber' ? 'bg-amber-500' : accent === 'green' ? 'bg-green-500' : 'bg-gray-400'
      }`}
    />
    <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">{label}</h4>
  </div>
);

interface ReceivedRequestCardProps {
  request: ReceivedRequest;
  onRespond: () => void;
  onDecline: () => void;
  onDismiss?: (needsConfirmation: boolean) => void;
  onOpenDetails?: () => void;
}

const ReceivedRequestCard = ({
  request,
  onRespond,
  onDecline,
  onDismiss,
  onOpenDetails,
}: ReceivedRequestCardProps) => {
  const expired = isExpired(request.expiresAt);
  const remaining = formatTimeRemaining(request.expiresAt);
  const accepted = request.responseStatus === 'available';
  const declined = request.responseStatus === 'unavailable';
  // Card is clickable when accepted (any time), declined, or once it has expired —
  // none of those can be acted on inline, so the only way to see what was being
  // asked is to open the details modal.
  const cardClickable = (accepted || declined || expired) && !!onOpenDetails;

  const handleCardClick = () => {
    if (cardClickable) onOpenDetails!();
  };

  const handleCardKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!cardClickable) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onOpenDetails!();
    }
  };

  return (
    <div
      role={cardClickable ? 'button' : undefined}
      tabIndex={cardClickable ? 0 : undefined}
      onClick={cardClickable ? handleCardClick : undefined}
      onKeyDown={cardClickable ? handleCardKeyDown : undefined}
      aria-label={cardClickable ? 'View availability request details' : undefined}
      className={`border rounded-lg p-4 transition-all bg-gradient-to-r ${
        accepted ? 'from-green-50/50 to-emerald-50/50 border-green-200' : 'from-blue-50/50 to-indigo-50/50 border-blue-200'
      } ${cardClickable ? `cursor-pointer hover:shadow-md focus:outline-none focus:ring-2 ${accepted ? 'focus:ring-green-400' : 'focus:ring-blue-400'}` : ''}`}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex-1 min-w-0">
          <h5 className="font-medium text-gray-900 truncate">
            {request.jobDescription || 'No description provided'}
          </h5>
          <p className="text-sm text-gray-600 mt-0.5">
            From <span className="font-medium">{request.senderName || 'Unknown'}</span>
          </p>
        </div>
        <Badge
          className={
            accepted
              ? 'bg-green-100 text-green-700 border-0'
              : declined || expired
                ? 'bg-gray-100 text-gray-600 border-0'
                : 'bg-amber-100 text-amber-700 border-0'
          }
        >
          {accepted ? 'Accepted' : declined ? 'Declined' : expired ? 'Expired' : 'Pending'}
        </Badge>
      </div>

      <div className="flex items-center gap-3 text-xs text-gray-600 flex-wrap mb-3">
        {request.jobLocation && (
          <span className="flex items-center gap-1">
            <MapPin className="w-3.5 h-3.5" />
            {request.jobLocation}
          </span>
        )}
        <span className="flex items-center gap-1">
          <Clock className={`w-3.5 h-3.5 ${expired ? 'text-red-500' : 'text-amber-500'}`} />
          {remaining}
        </span>
      </div>

      <div className="flex gap-2 flex-wrap">
        {/* An expired request can no longer be answered — the server rejects it. */}
        {!expired && (
          <Button
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onRespond();
            }}
            className={
              accepted || declined
                ? 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                : 'bg-blue-500 hover:bg-blue-600 text-white'
            }
          >
            {accepted || declined ? 'Change Response' : 'Respond'}
          </Button>
        )}
        {!accepted && !declined && !expired && (
          <Button
            size="sm"
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              onDecline();
            }}
          >
            Decline
          </Button>
        )}
        {/* Dismiss hides the row from the recipient's view without changing their
            response. Pending/accepted → subtle ghost + confirm modal. Declined/
            expired → primary, fires immediately (they're already in history). */}
        {onDismiss && (
          declined || expired ? (
            <Button
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onDismiss(false);
              }}
              className="bg-blue-500 hover:bg-blue-600 text-white"
            >
              <EyeOff className="w-3.5 h-3.5 mr-1.5" />
              Dismiss
            </Button>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                onDismiss(true);
              }}
              className="text-gray-500 hover:text-gray-700 hover:bg-gray-100"
            >
              <EyeOff className="w-3.5 h-3.5 mr-1.5" />
              Dismiss
            </Button>
          )
        )}
      </div>
    </div>
  );
};

// ---------- Sent sub-view ----------

interface SentViewProps {
  requests: SentRequest[];
  onOpenRequest: (id: string) => void;
  onDismiss: (req: SentRequest) => void;
}

const SentView = ({ requests, onOpenRequest, onDismiss }: SentViewProps) => {
  if (requests.length === 0) {
    return (
      <div className="bg-gray-50 rounded-lg p-6 text-center">
        <p className="text-gray-600 text-sm">
          No sent requests. Send an availability request from the Send Referral page.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {requests.map((req) => {
        const acceptedCount = req.recipients.filter((r) => r.responseStatus === 'available').length;
        const pendingCount = req.recipients.filter((r) => r.responseStatus === 'pending').length;
        const declinedCount = req.recipients.filter((r) => r.responseStatus === 'unavailable').length;
        const totalCount = req.recipients.length;
        const expired = isExpired(req.expiresAt);

        return (
          <div
            key={req.id}
            role="button"
            tabIndex={0}
            onClick={() => onOpenRequest(req.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onOpenRequest(req.id);
              }
            }}
            className="w-full text-left border border-gray-200 rounded-lg p-4 bg-gradient-to-r from-amber-50/40 to-orange-50/40 hover:shadow-md transition-all cursor-pointer"
          >
            <div className="flex items-start justify-between gap-3 mb-2">
              <h5 className="font-medium text-gray-900 truncate flex-1">
                {req.jobDescription.length > 100
                  ? `${req.jobDescription.substring(0, 100)}...`
                  : req.jobDescription}
              </h5>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Badge
                  className={
                    acceptedCount > 0
                      ? 'bg-green-100 text-green-700 border-0'
                      : expired
                        ? 'bg-red-100 text-red-700 border-0'
                        : 'bg-amber-100 text-amber-700 border-0'
                  }
                >
                  {acceptedCount > 0
                    ? `${acceptedCount} accepted`
                    : expired
                      ? 'Expired'
                      : 'Awaiting'}
                </Badge>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDismiss(req);
                  }}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-white/60 rounded transition-colors"
                  aria-label="Dismiss request"
                  title="Dismiss (hides from your Sent list)"
                >
                  <EyeOff className="w-3.5 h-3.5" />
                  <span>Dismiss</span>
                </button>
              </div>
            </div>

            <div className="flex items-center gap-3 text-xs text-gray-600 flex-wrap">
              {req.jobLocation && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5" />
                  {req.jobLocation}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Clock className={`w-3.5 h-3.5 ${expired ? 'text-red-500' : 'text-amber-500'}`} />
                {formatTimeRemaining(req.expiresAt)}
              </span>
              <span className="flex items-center gap-1">
                <Users className="w-3.5 h-3.5" />
                {totalCount} sent
              </span>
            </div>

            <div className="mt-2 flex gap-2 text-xs flex-wrap">
              {acceptedCount > 0 && (
                <span className="text-green-700">{acceptedCount} accepted</span>
              )}
              {pendingCount > 0 && (
                <span className="text-amber-700">{pendingCount} pending</span>
              )}
              {declinedCount > 0 && (
                <span className="text-gray-500">{declinedCount} declined</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ---------- Sent recipient detail modal ----------

interface SentRequestDetailModalProps {
  request: SentRequest;
  isOpen: boolean;
  onClose: () => void;
  selectedRecipientId: string | null;
  onSelectRecipient: (id: string | null) => void;
  onSendReferral: () => void;
  onSendToMore: () => void;
  onCancelRequest: () => void;
  onDismissRequest: () => void;
  onReopenRequest: (hoursToExtend: number) => void;
  isSendingReferral: boolean;
  isReopening: boolean;
}

const SentRequestDetailModal = ({
  request,
  isOpen,
  onClose,
  selectedRecipientId,
  onSelectRecipient,
  onSendReferral,
  onSendToMore,
  onCancelRequest,
  onDismissRequest,
  onReopenRequest,
  isSendingReferral,
  isReopening,
}: SentRequestDetailModalProps) => {
  const expired = isExpired(request.expiresAt);
  const accepted = request.recipients.filter((r) => r.responseStatus === 'available');
  const pending = request.recipients.filter((r) => r.responseStatus === 'pending');
  const declined = request.recipients.filter((r) => r.responseStatus === 'unavailable');

  const noOneAvailable = accepted.length === 0;
  const showTimedOutCtas = noOneAvailable && (expired || pending.length === 0);

  // Inline reopen picker state — when the sender clicks "Reopen Request" the
  // action buttons are replaced by a duration picker so we don't crowd three
  // CTAs alongside three time options.
  const [reopenPickerOpen, setReopenPickerOpen] = useState(false);
  const [hoursToExtend, setHoursToExtend] = useState<number>(24);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Availability Request" size="medium">
      <div className="space-y-5">
        {/* Job summary */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-2">
          <div className="flex items-start gap-2">
            <Briefcase className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-gray-800 break-words">{request.jobDescription}</p>
          </div>
          <div className="flex items-center gap-3 text-xs text-gray-600 flex-wrap">
            {request.jobLocation && (
              <span className="flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5" />
                {request.jobLocation}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Clock className={`w-3.5 h-3.5 ${expired ? 'text-red-500' : 'text-amber-500'}`} />
              {formatTimeRemaining(request.expiresAt)}
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" />
              Sent {new Date(request.createdAt).toLocaleDateString()}
            </span>
          </div>
        </div>

        {/* Recipients list */}
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-gray-700">
            Recipients ({request.recipients.length})
          </h4>

          {accepted.length > 0 && (
            <RecipientGroup
              label="Accepted"
              accent="green"
              recipients={accepted}
              selectable
              selectedId={selectedRecipientId}
              onSelect={onSelectRecipient}
            />
          )}
          {pending.length > 0 && (
            <RecipientGroup label="Pending" accent="amber" recipients={pending} />
          )}
          {declined.length > 0 && (
            <RecipientGroup label="Declined" accent="gray" recipients={declined} />
          )}

          {request.recipients.length === 0 && (
            <p className="text-sm text-gray-500 italic">No recipients on this request.</p>
          )}
        </div>

        {/* Footer CTAs */}
        <div className="border-t border-gray-200 pt-4">
          {showTimedOutCtas ? (
            reopenPickerOpen ? (
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Extend request for:</p>
                  <div className="grid grid-cols-3 gap-2">
                    {[2, 24, 48].map((hours) => (
                      <button
                        key={hours}
                        type="button"
                        onClick={() => setHoursToExtend(hours)}
                        className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                          hoursToExtend === hours
                            ? 'bg-blue-500 text-white'
                            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {hours === 24 ? '24 Hours' : hours === 48 ? '48 Hours' : '2 Hours'}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    onClick={() => onReopenRequest(hoursToExtend)}
                    disabled={isReopening}
                    className="flex-1 bg-blue-500 hover:bg-blue-600 text-white disabled:opacity-50"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${isReopening ? 'animate-spin' : ''}`} />
                    {isReopening ? 'Reopening...' : 'Confirm Reopen'}
                  </Button>
                  <Button
                    onClick={() => setReopenPickerOpen(false)}
                    variant="outline"
                    disabled={isReopening}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row gap-2">
                {expired && (
                  <Button
                    onClick={() => setReopenPickerOpen(true)}
                    className="flex-1 bg-blue-500 hover:bg-blue-600 text-white"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Reopen Request
                  </Button>
                )}
                <Button
                  onClick={onSendToMore}
                  variant={expired ? 'outline' : 'default'}
                  className={
                    expired
                      ? 'flex-1 border-blue-300 text-blue-700 hover:bg-blue-50'
                      : 'flex-1 bg-blue-500 hover:bg-blue-600 text-white'
                  }
                >
                  <Send className="w-4 h-4 mr-2" />
                  Send To More Partners
                </Button>
                <Button
                  onClick={onCancelRequest}
                  variant="outline"
                  className="flex-1 border-red-300 text-red-700 hover:bg-red-50"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Cancel Request
                </Button>
                <Button
                  onClick={onDismissRequest}
                  variant="outline"
                  className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  <EyeOff className="w-4 h-4 mr-2" />
                  Dismiss
                </Button>
              </div>
            )
          ) : (
            <Button
              onClick={onSendReferral}
              disabled={!selectedRecipientId || isSendingReferral}
              className="w-full bg-blue-500 hover:bg-blue-600 text-white disabled:opacity-50"
            >
              <Send className="w-4 h-4 mr-2" />
              {isSendingReferral ? 'Sending...' : 'Send Referral'}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};

interface RecipientGroupProps {
  label: string;
  accent: 'green' | 'amber' | 'gray';
  recipients: SentRequestRecipient[];
  selectable?: boolean;
  selectedId?: string | null;
  onSelect?: (id: string | null) => void;
}

const RecipientGroup = ({
  label,
  accent,
  recipients,
  selectable,
  selectedId,
  onSelect,
}: RecipientGroupProps) => {
  const accentClasses: Record<typeof accent, string> = {
    green: 'bg-green-50 border-green-200',
    amber: 'bg-amber-50 border-amber-200',
    gray: 'bg-gray-50 border-gray-200',
  };
  const dotClasses: Record<typeof accent, string> = {
    green: 'bg-green-500',
    amber: 'bg-amber-500',
    gray: 'bg-gray-400',
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <span className={`w-2 h-2 rounded-full ${dotClasses[accent]}`} />
        <h5 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
          {label} ({recipients.length})
        </h5>
      </div>
      <div className="space-y-2">
        {recipients.map((rec) => {
          const isSelected = selectable && selectedId === rec.recipientId;
          const Tag: any = selectable ? 'button' : 'div';
          return (
            <Tag
              key={rec.id}
              type={selectable ? 'button' : undefined}
              onClick={
                selectable
                  ? () => onSelect && onSelect(isSelected ? null : rec.recipientId)
                  : undefined
              }
              className={`w-full text-left flex items-center justify-between gap-3 p-3 rounded-md border ${accentClasses[accent]} ${
                selectable ? 'cursor-pointer hover:shadow-sm transition-all' : ''
              } ${isSelected ? 'ring-2 ring-blue-400' : ''}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                {selectable && (
                  <span
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                      isSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-300 bg-white'
                    }`}
                  >
                    {isSelected && <CheckCircle className="w-3 h-3 text-white" />}
                  </span>
                )}
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {rec.recipientName || 'Unknown'}
                  </p>
                  {rec.recipientTrade && (
                    <p className="text-xs text-gray-500 truncate">{rec.recipientTrade}</p>
                  )}
                  {rec.responseMessage && (
                    <p className="text-xs text-gray-600 italic mt-0.5 truncate">
                      "{rec.responseMessage}"
                    </p>
                  )}
                </div>
              </div>
            </Tag>
          );
        })}
      </div>
    </div>
  );
};

// ---------- Partner picker modal ----------

interface PartnerPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  existingRecipientIds: string[];
  jobTradeName: string | null;
  onConfirm: (recipientIds: string[]) => void;
  isSubmitting: boolean;
}

const PartnerPickerModal = ({
  isOpen,
  onClose,
  existingRecipientIds,
  jobTradeName,
  onConfirm,
  isSubmitting,
}: PartnerPickerModalProps) => {
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const connectionsQuery = useQuery({
    queryKey: ['/api/workflows/connections'],
    queryFn: async () => {
      const response = await api.get('/api/workflows/connections');
      // Endpoint returns array directly (no { success, data } envelope)
      return (Array.isArray(response) ? response : response?.data || []) as Connection[];
    },
    staleTime: 60 * 1000,
    enabled: isOpen,
  });

  // Reset selection when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedIds(new Set());
      setSearch('');
    }
  }, [isOpen]);

  const allConnections = (connectionsQuery.data || []).filter(
    (c) => !existingRecipientIds.includes(c.id)
  );

  // Filter by search
  const filtered = allConnections.filter((c) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      getConnectionDisplayName(c).toLowerCase().includes(q) ||
      (c.company || '').toLowerCase().includes(q) ||
      (c.primaryTrade || '').toLowerCase().includes(q)
    );
  });

  // Split by trade match (when jobTradeName provided)
  const { matched, unmatched } = useMemo(() => {
    if (!jobTradeName) return { matched: [] as Connection[], unmatched: filtered };
    const m: Connection[] = [];
    const u: Connection[] = [];
    for (const c of filtered) {
      if (connectionMatchesTrade(c, jobTradeName)) m.push(c);
      else u.push(c);
    }
    return { matched: m, unmatched: u };
  }, [filtered, jobTradeName]);

  const sortedMatched = [...matched].sort((a, b) =>
    compareNamesAsc(getConnectionDisplayName(a), getConnectionDisplayName(b))
  );
  const sortedUnmatched = [...unmatched].sort((a, b) =>
    compareNamesAsc(getConnectionDisplayName(a), getConnectionDisplayName(b))
  );

  const toggleSelected = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const matchedTradeLabel = jobTradeName;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Send To More Partners" size="medium">
      <div className="space-y-4">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Search by name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Connections list */}
        <div className="max-h-[50vh] overflow-y-auto pr-1 space-y-4">
          {connectionsQuery.isPending ? (
            <div className="space-y-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : allConnections.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6">
              No additional connected partners available.
            </p>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6">No partners match your search.</p>
          ) : (
            <>
              {sortedMatched.length > 0 && matchedTradeLabel && (
                <PartnerListGroup
                  heading={matchedTradeLabel}
                  items={sortedMatched}
                  selectedIds={selectedIds}
                  onToggle={toggleSelected}
                />
              )}
              {sortedUnmatched.length > 0 && (
                <PartnerListGroup
                  heading={sortedMatched.length > 0 && matchedTradeLabel ? 'Other Partners' : 'All Partners'}
                  items={sortedUnmatched}
                  selectedIds={selectedIds}
                  onToggle={toggleSelected}
                />
              )}
            </>
          )}
        </div>

        <div className="border-t border-gray-200 pt-4 flex flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button
            onClick={() => onConfirm(Array.from(selectedIds))}
            disabled={selectedIds.size === 0 || isSubmitting}
            className="flex-1 bg-blue-500 hover:bg-blue-600 text-white disabled:opacity-50"
          >
            {isSubmitting
              ? 'Sending...'
              : `Send to ${selectedIds.size} Partner${selectedIds.size === 1 ? '' : 's'}`}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

interface PartnerListGroupProps {
  heading: string;
  items: Connection[];
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
}

const PartnerListGroup = ({ heading, items, selectedIds, onToggle }: PartnerListGroupProps) => (
  <div>
    <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 px-1">
      {heading}
    </h5>
    <div className="space-y-1">
      {items.map((c) => {
        const isSelected = selectedIds.has(c.id);
        return (
          <button
            key={c.id}
            type="button"
            onClick={() => onToggle(c.id)}
            className={`w-full text-left flex items-center justify-between p-3 rounded-md border transition-all ${
              isSelected
                ? 'bg-blue-50 border-blue-300 ring-2 ring-blue-200'
                : 'bg-white border-gray-200 hover:bg-gray-50'
            }`}
          >
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-900 truncate">
                {getConnectionDisplayName(c)}
              </p>
              <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                {c.primaryTrade && <span className="truncate">{c.primaryTrade}</span>}
                {c.company && (
                  <>
                    <span>·</span>
                    <span className="truncate">{c.company}</span>
                  </>
                )}
              </div>
            </div>
            <span
              className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ml-3 ${
                isSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-300 bg-white'
              }`}
            >
              {isSelected && <CheckCircle className="w-3 h-3 text-white" />}
            </span>
          </button>
        );
      })}
    </div>
  </div>
);

export default AvailabilityRequestsPanel;
